import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Home as HomeIcon, Building2, Factory, Zap, ShieldCheck, Clock, Users, ThumbsUp } from "lucide-react";
import { BUSINESS, IMAGES, TRUST_ITEMS, SERVICES } from "@/data/site";
import { Reveal, MaskedLines, Eyebrow } from "@/components/motion";
import { setPageMeta } from "@/components/seo";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";

const SERVICE_ICONS = { residential: HomeIcon, commercial: Building2, industrial: Factory, level2: Zap };
const TRUST_ICONS = [Clock, ShieldCheck, Users, ThumbsUp];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col bg-ink overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="Electrician performing electrical work — LAD CO Electrical, Northern Rivers NSW"
          className="w-full h-[120%] object-cover opacity-40"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
      </motion.div>

      <motion.div className="relative flex-1 flex items-center container-site pt-32 pb-16" style={{ opacity: fade }}>
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 border border-volt/40 bg-volt/10 px-4 py-2"
            data-testid="hero-status-pill"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full bg-volt-bright opacity-60" />
              <span className="relative inline-flex h-2 w-2 bg-volt-bright" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt-bright">Serving the Northern Rivers, NSW</span>
          </motion.div>

          <MaskedLines
            lines={["Reliable Electrical", "Services Across the", "Northern Rivers"]}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white mt-7 leading-[1.05]"
            lineClassName=""
            baseDelay={0.2}
          />
          <motion.p
            className="text-gray-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            data-testid="hero-subheading"
          >
            Fast, friendly and experienced electrical services for residential, commercial and industrial customers.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 mt-9"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <Link to="/quote" className="btn-volt" data-testid="hero-quote-cta">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={BUSINESS.phoneHref} className="btn-ghost-light" data-testid="hero-call-cta">
              <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="relative border-t border-white/15"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
        data-testid="hero-stats-bar"
      >
        <div className="container-site grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/15">
          {[
            ["20+", "Years Experience"],
            ["Level 2", "Accredited Service Provider"],
            ["Local", "Family Business — Northern Rivers NSW"],
          ].map(([big, small], i) => (
            <div key={i} className="py-5 sm:py-6 sm:px-8 first:pl-0 flex items-baseline gap-3">
              <span className="font-heading text-xl sm:text-2xl font-extrabold uppercase text-volt-bright whitespace-nowrap">{big}</span>
              <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-white/60">{small}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const Home = () => {
  useEffect(() => {
    setPageMeta(
      "LAD CO Electrical | Level 2 Electrician Northern Rivers NSW",
      "Professional Level 2 electrical services across the Northern Rivers NSW. Residential, commercial and industrial electrical services."
    );
  }, []);

  return (
    <>
      <Hero />
      <Marquee />

      <section className="bg-ink border-b border-steel" data-testid="emergency-banner">
        <div className="container-site py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 border border-volt/40 bg-volt/10 px-4 py-2" data-testid="emergency-pill">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full bg-volt-bright opacity-60" />
                <span className="relative inline-flex h-2 w-2 bg-volt-bright" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt-bright">24/7 Emergency Service Available</span>
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white mt-5 leading-snug">
              Electrical Emergency? We're On Call.
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mt-4 leading-relaxed">
              LAD CO Electrical provides a 24/7 emergency electrical service across the Northern Rivers. If you need an electrician urgently, call any time.
            </p>
            <a href={BUSINESS.phoneHref} className="btn-volt mt-7" data-testid="emergency-call-cta">
              <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <img
              src={IMAGES.climb}
              alt="LAD CO Electrical linesman climbing a power pole beside the branded service ute"
              className="w-full aspect-[4/3] object-cover object-[50%_20%] border border-steel"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-white dot-texture-dark" data-testid="trust-bar">
        <div className="container-site py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {TRUST_ITEMS.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <Reveal key={item.title} delay={i * 0.08} className="bg-white">
                  <div className="p-8 h-full group transition-colors duration-300 hover:bg-ink" data-testid={`trust-item-${i}`}>
                    <span className="font-mono text-xs text-volt font-semibold">0{i + 1}</span>
                    <Icon className="w-7 h-7 text-volt mt-4" />
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-ink mt-4 transition-colors duration-300 group-hover:text-white">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed transition-colors duration-300 group-hover:text-gray-400">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-gray-100" data-testid="about-teaser">
        <div className="container-site py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-volt" aria-hidden="true" />
              <img
                src={IMAGES.about}
                alt="LAD CO Electrical electrician completing detailed electrical work"
                className="relative w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-ink text-white px-6 py-5">
                <span className="block font-heading text-3xl font-extrabold text-volt-bright">20+</span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1">Years Experience</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow>About LAD CO Electrical</Eyebrow>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink mt-4 leading-snug">
              Local Experience. Reliable Service.
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-6 leading-relaxed">
              LAD CO Electrical is a local family business servicing the Northern Rivers of NSW. With more than 20 years of experience, we provide comprehensive electrical services for residential, commercial and industrial customers.
            </p>
            <p className="text-gray-600 text-base sm:text-lg mt-4 leading-relaxed">
              We pride ourselves on being fast, friendly and professional while providing quality electrical solutions at an affordable price.
            </p>
            <Link to="/about" className="btn-ghost-dark mt-8" data-testid="about-learn-more">
              Learn More About LAD CO Electrical <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F4F6F5] border-t border-gray-200" data-testid="services-section">
        <div className="container-site py-20 lg:py-28">
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <Eyebrow>What We Do</Eyebrow>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink mt-4">
                Electrical Services
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mt-4 max-w-2xl leading-relaxed">
                Comprehensive electrical services for residential, commercial and industrial customers across the Northern Rivers.
              </p>
            </div>
            <Link to="/services" className="btn-ghost-dark shrink-0" data-testid="services-view-all">
              All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {SERVICES.map((service, i) => {
              const Icon = SERVICE_ICONS[service.id];
              return (
                <Reveal key={service.id} delay={i * 0.08}>
                  <div
                    className="group bg-white border border-gray-200 p-8 h-full flex flex-col transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-volt hover:shadow-[0_16px_40px_rgba(11,15,14,0.08)]"
                    data-testid={`service-card-${service.id}`}
                  >
                    <span className="w-12 h-12 bg-ink flex items-center justify-center transition-colors duration-300 group-hover:bg-volt">
                      <Icon className="w-5 h-5 text-white" />
                    </span>
                    <h3 className="font-heading text-xl font-bold tracking-tight text-ink mt-6">{service.name}</h3>
                    <p className="text-gray-500 text-sm mt-3 leading-relaxed flex-1">{service.text}</p>
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-wider text-volt mt-6 group-hover:gap-3 transition-all duration-300"
                    >
                      Learn More <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-ink dot-texture relative overflow-hidden" data-testid="level2-feature">
        <div className="container-site py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow dark>Accreditation</Eyebrow>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white mt-4 leading-[1.05]">
              Level 2 Accredited <span className="text-volt-bright">Service Provider</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mt-6 leading-relaxed">
              LAD CO Electrical is a Level 2 accredited service provider operating across the Northern Rivers region of NSW.
            </p>
            <p className="text-gray-400 text-base sm:text-lg mt-4 leading-relaxed">
              Level 2 accreditation sits within the NSW Accredited Service Provider scheme and recognises providers qualified to carry out certain work connected to the electricity network. Talk to the team about your Level 2 requirements.
            </p>
            <Link to="/contact" className="btn-volt mt-9" data-testid="level2-talk-cta">
              Talk to LAD CO Electrical <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img
                src={IMAGES.buildSite}
                alt="LAD CO Electrical service vehicle on site at a job in the Northern Rivers NSW"
                className="w-full aspect-[4/3] object-cover border border-steel"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 bg-volt px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white font-semibold">
                Level 2 ASP
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F4F6F5] border-t border-gray-200" data-testid="photo-strip">
        <div className="container-site py-20 lg:py-28">
          <Reveal>
            <Eyebrow>On the Tools</Eyebrow>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink mt-4">
              Our Work Across the Northern Rivers
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-4 max-w-2xl leading-relaxed">
              Real jobs, real team — a look at LAD CO Electrical out on site.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-12">
            {[
              { src: IMAGES.ute, alt: "The LAD CO Electrical service ute with ladder rack, ready for work" },
              { src: IMAGES.climbWide, alt: "LAD CO Electrical linesman up a power pole with the service ute below" },
              { src: IMAGES.tempSupply, alt: "LAD CO Electrical working on a site power connection in the Northern Rivers" },
              { src: IMAGES.switchboard, alt: "LAD CO Electrical electrician working on a home switchboard" },
              { src: IMAGES.buildSite, alt: "The LAD CO Electrical ute on site at a new build" },
              { src: IMAGES.battery, alt: "LAD CO Electrical installing a home battery energy storage system" },
            ].map((photo, i) => (
              <Reveal key={photo.src} delay={i * 0.08}>
                <div className="group relative overflow-hidden" data-testid={`photo-strip-${i}`}>
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/20" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Home;
